import store from 'source/store';
import { useBoardFiltersInURL } from 'source/composables/useBoardFiltersInURL';

function isUserPermitted(workforce_id) {
    const user_profile = store.state.auth.user?.user_profile;
    if (!user_profile || user_profile.is_locked) return false;

    const workforces = user_profile.workforce_ids;
    return workforces.includes(parseInt(workforce_id));
}

async function checkBoardRoute (to, from, next) {
    const board_id = to.params.board_id;
    const workforce_id = to.params.workforce_id;
    if (!workforce_id) return;

    if (!isUserPermitted(workforce_id))
        next({ name: 'access-denied', replace: true });

    const user = store.state.auth.user;

    if (
        (!from.name || ['landing page', 'signin', 'overview'].includes(from.name)) &&
        board_id && board_id.toString() !== '-1'
    ) {
        const isBoardMember = await checkIfUserIsABoardMember(board_id);
        if (!isBoardMember) {
            next({ name: 'access-denied', replace: true });
        }
    }
    else if ((board_id && board_id.toString() === '-1') || !board_id) {
        const boardsByWorkforce = await store.dispatch('board/fetchWorkforceBoards', {
            workforce_id, members__in: user.user_profile.pk, size_per_request: 1 });
        if (boardsByWorkforce?.total_count === 0 ) {

            // For overview route
            // Passing user id for org exec since org execs dont have an employment
            let employment_id = user.pk;

            if (user.user_profile.employment_id) {
                employment_id = user.user_profile.employment_id;
            }
            next({ name: 'overview', params: { ...to.params, employment_id }, replace: true });
        } else {
            to.params.board_id = boardsByWorkforce.objects[0].pk;
            next({ ...to, replace: true });
        }
    }
    if (to.name === 'board-main') await checkCardQuery(to, from, next);
    next();
}

async function checkIfUserIsABoardMember(board_id) {
    const user = store.state.auth.user;
    if (user === null) return false;

    const boards = await store.dispatch('board/fetchBoards', { pk: board_id, members__in: user.user_profile.pk });
    return boards.length > 0;
}

async function checkCardQuery(to, from, next) {
    const cardIDQuery = to.query?.card_id;
    if (!cardIDQuery) return next();

    const boardId = to.params.board_id;

    try {
        const response = await store.dispatch('board/checkCardExistence',
            { pk: cardIDQuery, board_id: boardId });

        if (response && response.found) {
            // queried using reference number
            if (response.doer_pk) {
                return next({
                    name: to.name,
                    params: to.params,
                    query: { ...to.query, card_id: response.doer_pk },
                    replace: true
                });
            }
            else return next();
        }
        else next({ name: 'page-not-found' });
    } catch (error) {
        console.error('Error checking card existence:', error);
        next();
    }
}

function checkUserAccess(to, from, next) {
    const workforce_id = to.params?.workforce_id;
    if (!isUserPermitted(workforce_id)) {
        next({ name: 'access-denied' });
    } else {
        next();
    }
}

const routes = [
    {
        path: '/card-:card_id',
        name: 'card-short-link',
        meta: {
            title: 'Card link - HQZen',
            canDisplayGlobalModals: false,
        },
        async beforeEnter(to, from, next) {
            const cardId = to.params.card_id;

            try {
                const card = await store.dispatch('board/getColumnCard', cardId);
                if (!card) {
                    console.warn('No card found for id:', cardId);
                    return next({ name: 'page-not-found' });
                }
                store.commit('board/setQueriedCard', card);
                const boardId = card.column?.board?.pk;
                const workforceId = card.column?.board?.workforce?.pk;
                const companyId = card?.company_id;

                if (!boardId || !workforceId || !companyId) {
                    console.error('Missing IDs for redirect:', {
                        boardId,
                        workforceId,
                        companyId,
                        card,
                    });
                    return next({ name: 'page-not-found' });
                }

                // Redirect to existing board route
                return next({
                    name: 'board-main',
                    params: {
                        company_id: companyId,
                        workforce_id: workforceId,
                        board_id: boardId,
                    },
                    query: {
                        card_id: cardId,
                    },
                    replace: true,
                });
            } catch (error) {
                console.error('Error resolving card location:', error);
                return next({ name: 'page-not-found' });
            }
        },
    },{
    path: 'board',
    name: 'board-page',
    component: () =>
        import ('source/pages/root/BoardPageGroup.vue'),
    meta: {
        title: 'Board - HQZen',
    },

    children: [{
        path: ':board_id',
        name: 'board-main',
        component: () =>
            import ('source/pages/board/main-board/BoardViewPage.vue'),
        beforeEnter: async(to, from, next) => {
            const { fromURLToBoardFiltersFunc } = useBoardFiltersInURL();
            await fromURLToBoardFiltersFunc({ routeArgs: to });
            await checkBoardRoute(to, from, next);
        },
        props: true,
        meta: {
            title: 'Main Board Page - HQZen',
            isBoardRoute: true
        },
    }, {
        path: ':board_id/set-up-form/:engagement_form_id?',
        name: 'command-board-setup',
        beforeEnter: checkBoardRoute,
        component: () =>
            import ('source/pages/board/main-board/CommandBoardSetupPage.vue'),
        props: true,
        meta: {
            title: 'Set up automated form - HQZen',
            isBoardRoute: true
        },
    },{
        path: ':board_id/chatbot-settings/:template_id?',
        name: 'chatbot-settings',
        beforeEnter: checkBoardRoute,
        component: () =>
            import ('source/pages/board/main-board/BoardChatBotSettings.vue'),
        meta: {
            title: 'AI Settings - HQZen',
            isBoardRoute: true
        },
        props: true,
    },
    {
        path: ':board_id/board-analytics',
        name: 'board-analytics',
        beforeEnter: checkBoardRoute,
        component: () =>
            import ('source/pages/board/main-board/CardActivity.vue'),
        props: true,
        meta: {
            title: 'Board Analytics - HQZen',
        },
    },
    {
        path: ':board_id/gallery',
        name: 'board-image-gallery-page',
        beforeEnter: checkBoardRoute,
        component: () => import ('source/pages/milestones/ImageGalleryPage.vue'),
        props: true,
        meta: {
            title: 'Image Gallery - HQZen',
        }
    }]
}, {
    path: 'workforce-board',
    name: 'workforce-board',
    beforeEnter: checkUserAccess,
    component: () =>
        import ('source/pages/board/workforce-board/WorkforceBoardPage.vue'),
    meta: {
        title: 'Shared Board - HQZen',
    }
}];

export default routes;
